import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, isUserDisabled } from '../../services/userProfile';
import GoogleIcon from './GoogleIcon';
import PasswordField from './PasswordField';

function getErrorMessage(code) {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/popup-blocked':
      return 'Pop-up blocked. Allow pop-ups and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account with this email already exists. Sign in with email/password.';
    default:
      return 'Sign up failed. Please try again.';
  }
}

/**
 * Shared email/password + Google sign-up form.
 * @param {{ onSuccess: (uid: string, profile: object | null) => void | Promise<void>; isPartnerSignup?: boolean; idPrefix?: string; autoFocus?: boolean }} props
 */
const SignUpForm = ({
  onSuccess,
  isPartnerSignup = false,
  idPrefix = '',
  autoFocus = true,
}) => {
  const { signUp, signInWithGoogle } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const usernameId = `${idPrefix}username`;
  const emailId = `${idPrefix}email`;
  const passwordId = `${idPrefix}password`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const cred = await signUp(email, password, displayName, {
        role: isPartnerSignup ? 'partner' : 'user',
      });
      const profile = await getUserProfile(cred.user.uid);
      await onSuccess(cred.user.uid, profile);
    } catch (err) {
      console.error('Signup error:', err);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      const cred = await signInWithGoogle({
        role: isPartnerSignup ? 'partner' : 'user',
      });
      const profile = await getUserProfile(cred.user.uid);
      if (isUserDisabled(profile)) {
        setError('This account has been disabled. Ask an admin to Enable it in Users.');
        return;
      }
      if (isPartnerSignup && profile?.role && profile.role !== 'partner') {
        setError(
          'This Google account already exists with a different role. Ask an admin to set your role to partner.'
        );
        return;
      }
      await onSuccess(cred.user.uid, profile);
    } catch (err) {
      console.error('Google signup error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('');
      } else {
        setError(getErrorMessage(err.code));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && <div className="login-error">{error}</div>}

      <button
        type="button"
        className="login-google-btn"
        onClick={handleGoogle}
        disabled={loading}
      >
        <GoogleIcon />
        Continue with Google
      </button>

      <div className="login-divider pub-mono" aria-hidden="true">
        <span>or</span>
      </div>

      <form onSubmit={handleSubmit} className="login-form">
        <div className="login-field">
          <label htmlFor={usernameId}>Username</label>
          <input
            id={usernameId}
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Optional"
            autoComplete="username"
          />
        </div>

        <div className="login-field">
          <label htmlFor={emailId}>Email</label>
          <input
            id={emailId}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoFocus={autoFocus}
          />
        </div>

        <PasswordField
          id={passwordId}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 6 characters"
          required
          minLength={6}
          autoComplete="new-password"
        />

        <button type="submit" className="login-submit-btn" disabled={loading}>
          {loading
            ? 'Creating account…'
            : isPartnerSignup
              ? 'Create partner account'
              : 'Create account'}
        </button>
      </form>
    </>
  );
};

export default SignUpForm;
